import { Truck, CircleDot, Info, Sparkles, AlertTriangle, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PostLoadCard, PostLoadCardHeader, FieldInput } from "./PostLoadCard";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import { cn } from "@/lib/utils";

function AIHintRow({ totalMiles }: { totalMiles?: number | null }) {
  const hasMiles = totalMiles != null && totalMiles > 0;
  const days = hasMiles ? Math.max(1, Math.round(totalMiles / 600)) : null;

  return (
    <div
      className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[10px]"
      style={{ backgroundColor: "hsl(170 85% 32% / 0.08)" }}
    >
      <Sparkles className="h-3 w-3 flex-shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }} />
      <span className="text-muted-foreground">
        {hasMiles ? (
          <>
            Lane distance: <strong style={{ color: "hsl(var(--brand-teal-deep))" }}>{Math.round(totalMiles).toLocaleString()} mi</strong>
            {" • "}Est. transit: <strong style={{ color: "hsl(var(--brand-teal-deep))" }}>~{days} {days === 1 ? "day" : "days"}</strong>
          </>
        ) : (
          "Set origin & destination to see lane info"
        )}
      </span>
    </div>
  );
}

function AIWarningRow({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[10px]"
      style={{ backgroundColor: "hsl(38 92% 50% / 0.08)" }}
    >
      <AlertTriangle className="h-3 w-3 flex-shrink-0" style={{ color: "hsl(var(--warning))" }} />
      <span className="text-muted-foreground">
        Weight higher than usual. Double-check before posting.
      </span>
    </div>
  );
}

/* ── Reusable toggle chip row (exported for the guided wizard) ── */
export interface ToggleChip {
  label: string;
  value: string;
}

export function ToggleChipRow({
  chips,
  currentValue,
  onSelect,
  onClear,
}: {
  chips: ToggleChip[];
  currentValue: string;
  onSelect: (value: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {chips.map((chip) => {
        const isActive = currentValue === chip.value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => isActive ? onClear() : onSelect(chip.value)}
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium transition-colors duration-150 border",
              isActive
                ? "border-primary bg-primary/10 text-primary"
                : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/50"
            )}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Comment toggle chips (multi-select, appends/removes from text) — exported for the guided wizard ── */
export const COMMENT_CHIPS = [
  "FCFS", "Appointment Required", "Drop & Hook", "No Touch",
  "Driver Assist", "Hazmat", "Team Required", "Blind Shipment",
  "Pre-cool Required", "Liftgate", "Pallet Jack", "Oversize", "Tarp Required",
];

export function CommentChipRow({
  comments,
  onCommentsChange,
}: {
  comments: string;
  onCommentsChange: (v: string) => void;
}) {
  const togglePhrase = (phrase: string) => {
    // Check if phrase is already in comments (case-insensitive)
    const regex = new RegExp(`(,\\s*)?\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b(\\s*,)?`, 'i');
    if (regex.test(comments)) {
      // Remove
      let updated = comments.replace(regex, (match, before, after) => {
        // If both comma before and after, keep one comma
        if (before && after) return ', ';
        return '';
      }).trim();
      // Clean up leading/trailing commas and extra spaces
      updated = updated.replace(/^[,\s]+/, '').replace(/[,\s]+$/, '').replace(/,\s*,/g, ',').replace(/\s{2,}/g, ' ');
      onCommentsChange(updated);
    } else {
      // Append
      const sep = comments.trim() ? ', ' : '';
      onCommentsChange(comments.trim() + sep + phrase);
    }
  };

  const isActive = (phrase: string) => {
    const regex = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(comments);
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {COMMENT_CHIPS.map((phrase) => {
        const active = isActive(phrase);
        return (
          <button
            key={phrase}
            type="button"
            onClick={() => togglePhrase(phrase)}
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium transition-colors duration-150 border",
              active
                ? "border-primary bg-primary/10 text-primary"
                : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/50"
            )}
          >
            {phrase}
          </button>
        );
      })}
    </div>
  );
}

export const EQUIPMENT_CHIPS: ToggleChip[] = [
  { label: "Van", value: "van" },
  { label: "Reefer", value: "reefer" },
  { label: "Flatbed", value: "flatbed" },
];

export const LENGTH_CHIPS: ToggleChip[] = [
  { label: "48", value: "48" },
  { label: "53", value: "53" },
];

export const WEIGHT_CHIPS: ToggleChip[] = [
  { label: "40k", value: "40000" },
  { label: "41k", value: "41000" },
  { label: "42k", value: "42000" },
  { label: "42.5k", value: "42500" },
  { label: "43k", value: "43000" },
  { label: "44k", value: "44000" },
  { label: "45k", value: "45000" },
];

interface EquipmentDetailsCardProps {
  loadType: string;
  equipmentType: string;
  length: string;
  weight: string;
  commodity: string;
  referenceNumber?: string;
  comments: string;
  totalMiles?: number | null;
  onLoadTypeChange: (v: string) => void;
  onEquipmentTypeChange: (v: string) => void;
  onLengthChange: (v: string) => void;
  onWeightChange: (v: string) => void;
  onCommodityChange: (v: string) => void;
  onReferenceNumberChange?: (v: string) => void;
  onCommentsChange: (v: string) => void;
  errors?: Record<string, string>;
  /** Optional second field (LR Direct): market type — spot / dedicated / contract.
   *  When provided, the capacity row switches to Full / Partial colored chips. */
  loadNature?: string;
  onLoadNatureChange?: (v: string) => void;
  /** LR Direct only: save current equipment type as the shipper's default. */
  onSaveEquipmentDefault?: () => void;
  savingEquipmentDefault?: boolean;
  equipmentDefaultSaved?: boolean;
}

/* ── Colored semantic chips (same tokens as the LR Direct board) ── */
const SELECT_CHIP_TOKENS: Record<string, string> = {
  FTL: "ftl",
  LTL: "ltl",
  spot: "spot",
  dedicated: "dedicated",
  contract: "contract",
};

export function ColorChipRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-[10px] font-medium text-muted-foreground block mb-1">{label}</label>
      <div className="flex items-center gap-1.5 flex-wrap">
        {options.map((o) => {
          const isSelected = value === o.value;
          const token = SELECT_CHIP_TOKENS[o.value] ?? "other";
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              aria-pressed={isSelected}
              className={cn(
                "inline-flex items-center rounded-md px-2 h-6 text-[10px] font-bold uppercase tracking-wide border transition-all",
                !isSelected && "border-border/60 bg-transparent text-muted-foreground hover:bg-muted/60",
              )}
              style={isSelected ? {
                backgroundColor: `hsl(var(--chip-${token}-bg))`,
                color: `hsl(var(--chip-${token}-text))`,
                borderColor: `hsl(var(--chip-${token}-border))`,
                boxShadow: `0 0 0 1px hsl(var(--chip-${token}-border))`,
              } : undefined}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function ChoiceRow({
  label,
  options,
  value,
  onChange,
}: {
  label?: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      {label && (
        <label className="text-[10px] font-medium text-muted-foreground block mb-1">
          {label}
        </label>
      )}
      <div className="flex items-center gap-3">
        {options.map((t) => {
          const isSelected = value === t.value;
          return (
            <label
              key={t.value}
              className={`flex items-center gap-1.5 text-[11px] cursor-pointer transition-colors ${
                isSelected ? "font-semibold" : "font-medium opacity-60"
              }`}
              style={{ color: isSelected ? "hsl(var(--brand-teal-deep))" : undefined }}
              onClick={() => onChange(t.value)}
            >
              <CircleDot
                className="h-3.5 w-3.5"
                style={{
                  color: isSelected
                    ? "hsl(var(--brand-teal-deep))"
                    : "hsl(var(--muted-foreground))",
                }}
              />
              {t.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function EquipmentDetailsCard({
  loadType,
  equipmentType,
  length,
  weight,
  commodity,
  referenceNumber = "",
  comments,
  totalMiles,
  onLoadTypeChange,
  onEquipmentTypeChange,
  onLengthChange,
  onWeightChange,
  onCommodityChange,
  onReferenceNumberChange,
  onCommentsChange,
  errors,
  loadNature,
  onLoadNatureChange,
  onSaveEquipmentDefault,
  savingEquipmentDefault = false,
  equipmentDefaultSaved = false,
}: EquipmentDetailsCardProps) {
  const weightNum = parseInt(weight.replace(/\D/g, ""), 10);
  const showWeightWarning = !isNaN(weightNum) && weightNum > 45000;

  const splitMode = !!onLoadNatureChange;

  const loadTypes = splitMode
    ? [
        { value: "FTL", label: "Full" },
        { value: "LTL", label: "Partial" },
      ]
    : [
        { value: "FTL", label: "Full" },
        { value: "LTL", label: "Partial" },
        { value: "contract", label: "Contract" },
      ];

  const loadNatures = [
    { value: "spot", label: "Spot" },
    { value: "dedicated", label: "Dedicated" },
    { value: "contract", label: "Contract" },
  ];


  return (
    <PostLoadCard variant="console">
      <PostLoadCardHeader
        icon={<Truck className="h-3.5 w-3.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />}
        title="Equipment Details"
        demoHintKey="section.equipmentDetails"
      />
      <div className="px-3 pb-3 space-y-2">
        {splitMode ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ColorChipRow
              label="Capacity"
              options={loadTypes}
              value={loadType}
              onChange={onLoadTypeChange}
            />
            <ColorChipRow
              label="Market Type"
              options={loadNatures}
              value={loadNature || "spot"}
              onChange={onLoadNatureChange!}
            />
          </div>
        ) : (
          <ChoiceRow
            options={loadTypes}
            value={loadType}
            onChange={onLoadTypeChange}
          />
        )}


        <div>
          <label className="text-[10px] font-medium text-muted-foreground block mb-1">
            Equipment Type <span className="text-destructive">*</span>
          </label>
          <EquipmentSelect
            value={equipmentType}
            onChange={(val) => onEquipmentTypeChange(val as string)}
            mode="single"
            placeholder="Equipment Type"
            className={`h-9 text-xs ${errors?.equipmentType ? "border-destructive" : ""}`}
          />
          {errors?.equipmentType && <p className="text-[10px] text-destructive mt-0.5">{errors.equipmentType}</p>}

          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <ToggleChipRow
              chips={EQUIPMENT_CHIPS}
              currentValue={equipmentType}
              onSelect={onEquipmentTypeChange}
              onClear={() => onEquipmentTypeChange("")}
            />
            {onSaveEquipmentDefault && (
              <>
                <button
                  type="button"
                  disabled={!equipmentType || savingEquipmentDefault}
                  onClick={onSaveEquipmentDefault}
                  className="text-[10px] font-semibold underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
                  style={{ color: "hsl(var(--brand-teal-deep))" }}
                >
                  {savingEquipmentDefault ? "Saving..." : "Save as default"}
                </button>
                {equipmentDefaultSaved && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium"
                    style={{ color: "hsl(var(--brand-teal-deep))" }}
                  >
                    <Check className="h-3 w-3" />
                    Saved
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <FieldInput
              label="Length (ft)"
              value={length}
              onChange={(v) => {
                if (v === "" || /^\d*\.?\d*$/.test(v)) {
                  const n = parseFloat(v);
                  if (v === "" || n <= 99) onLengthChange(v);
                }
              }}
              error={errors?.length}
            />
            <ToggleChipRow
              chips={LENGTH_CHIPS}
              currentValue={length}
              onSelect={onLengthChange}
              onClear={() => onLengthChange("")}
            />
          </div>
          <div>
            <FieldInput
              label="Weight (lbs.)"
              required
              value={weight}
              onChange={(v) => {
                if (v === "" || /^\d+$/.test(v)) {
                  const n = parseInt(v);
                  if (v === "" || n <= 99999) onWeightChange(v);
                }
              }}
              error={errors?.weight}
            />
            <ToggleChipRow
              chips={WEIGHT_CHIPS}
              currentValue={weight}
              onSelect={onWeightChange}
              onClear={() => onWeightChange("")}
            />
          </div>
        </div>


        <FieldInput label="Commodity" value={commodity} onChange={onCommodityChange} />
        <FieldInput label="Reference #" value={referenceNumber} onChange={v => onReferenceNumberChange?.(v)} />

        <AIHintRow totalMiles={totalMiles} />
        <AIWarningRow visible={showWeightWarning} />

        <Textarea
          placeholder="Comments"
          className="text-xs min-h-[60px] resize-none"
          value={comments}
          onChange={(e) => onCommentsChange(e.target.value)}
        />
        <CommentChipRow comments={comments} onCommentsChange={onCommentsChange} />

        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Info className="h-3 w-3 flex-shrink-0" />
          <span>If you want to add contacts to comments, use standard formats.</span>
        </div>
      </div>
    </PostLoadCard>
  );
}
